"use client";

// Extracted from app/page.tsx (lines 33–65, 107–241, 251–374).
// Owns: activePanorama state, deep-link hash router, tour navigation, back-stack.
//
// Key risk: hash router effect has deps [data, activePanorama?.id] — both are
// internal or passed as params, so behaviour is unchanged.
//
// Key mitigation: featureToActivePanorama() deduplicates the identical
// ActivePanorama shape that was built in 4 separate places.

import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { PanoramaFeatureCollection } from "@/api-client";
import { MapViewerHandle, SpiderFeature, ScreenOrigin } from "@/features/map";
import { ActivePanorama } from "../types";

// ── Private helper ────────────────────────────────────────────────────────────
/** Map a raw GeoJSON feature's properties to the ActivePanorama shape. */
function featureToActivePanorama(
  p: any,
  overrides?: Partial<ActivePanorama>,
): ActivePanorama {
  return {
    id:                p.id,
    baseTileUrl:       p.base_tile_url || "",
    previewUrl:        p.preview_url,
    width:             p.width,
    height:            p.height,
    poseHeading:       p.pose_heading,
    posePitch:         p.pose_pitch,
    poseRoll:          p.pose_roll,
    nadirPatchUrl:     p.nadir_patch_url,
    captureDate:       p.capture_date,
    creatorName:       p.creator_name,
    isVerifiedCreator: p.is_verified_creator,
    ownerId:           p.user_id,
    defaultYaw:        p.default_yaw ?? 0,
    defaultPitch:      p.default_pitch,
    defaultZoom:       p.default_zoom,
    introPanEnabled:   p.intro_pan_enabled ?? true,
    postcardEnabled:   p.postcard_enabled ?? true,
    postcardWatermark: p.postcard_watermark ?? null,
    spatialAudioEnabled: p.spatial_audio_enabled ?? true,
    timePairPanoramaId:  p.time_pair_panorama_id ?? null,
    timePairBaseTileUrl: p.time_pair_base_tile_url ?? null,
    ...overrides,
  };
}

// ─────────────────────────────────────────────────────────────────────────────

export function useActivePanorama(data: PanoramaFeatureCollection | null) {
  // isHashChecked starts false so the server HTML renders the loading screen by
  // default. useLayoutEffect sets it to true (+ pendingPanoramaId if there's a
  // hash) synchronously before the browser paints, so normal page loads never
  // show the loading screen and deep-link loads show it from the very first paint.
  const [isHashChecked,    setIsHashChecked]    = useState(false);
  const [pendingPanoramaId, setPendingPanoramaId] = useState<string | null>(null);
  useLayoutEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    const id   = new URLSearchParams(hash).get("pano");
    if (id) setPendingPanoramaId(id);
    setIsHashChecked(true);
  }, []);

  const [activePanorama, setActivePanorama]         = useState<ActivePanorama | null>(null);
  const [viewerOrigin, setViewerOrigin]             = useState<ScreenOrigin>({ x: 0, y: 0 });
  const [isTourTransitioning, setIsTourTransitioning] = useState(false);
  const [tourHistory, setTourHistory]               = useState<ActivePanorama[]>([]);
  // Tracks where the viewer was opened from, to decide what "back" means.
  // "map" = map marker click, "library" = dashboard library card, "external" = browser history
  // (profile page / deep link), "tourManager" = Virtual Tour Manager's per-photo view button
  const [viewerSource, setViewerSource]             = useState<"map" | "library" | "external" | "tourManager">("map");
  // Which collection to reopen the Tour Manager into when Back is pressed from a
  // "tourManager"-sourced viewer session.
  const [tourManagerCollectionId, setTourManagerCollectionId] = useState<string | null>(null);

  /** Imperative handle to MapViewer — used for the hash-driven cinematic fly-to. */
  const mapViewerRef    = useRef<MapViewerHandle>(null);
  /** Prevents the hashchange listener from re-triggering while an animation is in flight. */
  const isNavigatingRef = useRef(false);
  /** Mirrors activePanorama?.id so the hash-router closure is always fresh without adding activePanorama to its dep array. */
  const activePanoramaIdRef = useRef<string | undefined>(undefined);
  activePanoramaIdRef.current = activePanorama?.id;

  // ── Handlers ────────────────────────────────────────────────────────────────

  /** Map click → open viewer (called by MapViewer after the circle-expand animation). */
  const handleSelectPanorama = useCallback((feature: SpiderFeature, origin: ScreenOrigin) => {
    isNavigatingRef.current = false;
    setViewerSource("map");
    setViewerOrigin(origin);
    setActivePanorama({
      id:                feature.id,
      baseTileUrl:       feature.baseTileUrl,
      previewUrl:        feature.previewUrl,
      width:             feature.width,
      height:            feature.height,
      poseHeading:       feature.poseHeading,
      posePitch:         feature.posePitch,
      poseRoll:          feature.poseRoll,
      nadirPatchUrl:     feature.nadirPatchUrl,
      captureDate:       feature.captureDate,
      creatorName:       feature.creatorName,
      isVerifiedCreator: feature.isVerifiedCreator,
      ownerId:           feature.ownerId,
      defaultYaw:        feature.defaultYaw,
      defaultPitch:      feature.defaultPitch,
      defaultZoom:       feature.defaultZoom,
      introPanEnabled:   (feature as any).introPanEnabled,
      postcardEnabled:   (feature as any).postcardEnabled   ?? true,
      postcardWatermark: (feature as any).postcardWatermark ?? null,
      spatialAudioEnabled: (feature as any).spatialAudioEnabled ?? true,
    });
    if (typeof window !== "undefined" && !window.location.hash.includes(`pano=${feature.id}`)) {
      // Do not hardcode zoom — let usePSVCore use the saved defaultZoom from DB
      window.history.replaceState(null, "", `#pano=${feature.id}`);
    }
  }, []);

  /** Open a panorama directly from the dashboard (bypasses map fly-to). */
  const handleOpenPanoramaFromDashboard = useCallback((panoramaId: string) => {
    const feature = data?.features.find((f: any) => f.properties?.id === panoramaId);
    if (!feature) return;
    const p = (feature as any).properties;
    setViewerSource("library");
    setViewerOrigin({
      x: typeof window !== "undefined" ? window.innerWidth  / 2 : 400,
      y: typeof window !== "undefined" ? window.innerHeight / 2 : 300,
    });
    setActivePanorama(featureToActivePanorama(p));
    window.history.replaceState(null, "", `#pano=${p.id}`);
  }, [data]);

  /** Open a panorama from the Virtual Tour Manager's per-photo view button —
   *  Back returns to the Tour Manager, reopened on this same collection. */
  const handleOpenPanoramaFromTourManager = useCallback((panoramaId: string, collectionId: string) => {
    const feature = data?.features.find((f: any) => f.properties?.id === panoramaId);
    if (!feature) return;
    const p = (feature as any).properties;
    setViewerSource("tourManager");
    setTourManagerCollectionId(collectionId);
    setViewerOrigin({
      x: typeof window !== "undefined" ? window.innerWidth  / 2 : 400,
      y: typeof window !== "undefined" ? window.innerHeight / 2 : 300,
    });
    setActivePanorama(featureToActivePanorama(p));
    window.history.replaceState(null, "", `#pano=${p.id}`);
  }, [data]);

  /** Direct viewer-to-viewer portal transition. */
  const handleTourNavigate = useCallback((targetId: string, targetHeading?: number) => {
    const feature = data?.features.find((f) => (f.properties as any).id === targetId);
    if (!feature) return;
    const p = feature.properties as any;

    if (activePanorama) {
      setTourHistory((h) => [...h, { ...activePanorama, arrivedViaPortal: false }]);
    }

    setIsTourTransitioning(true);
    setTimeout(() => {
      setActivePanorama(featureToActivePanorama(p, {
        defaultYaw:        targetHeading !== undefined ? targetHeading : (p.default_yaw ?? 0),
        arrivedViaPortal:  true,
      }));
      window.history.replaceState(null, "", `#pano=${p.id}`);
      setTimeout(() => setIsTourTransitioning(false), 600);
    }, 420);
  }, [data, activePanorama]);

  /** Navigate back to the previous panorama in the tour history. */
  const handleBack = useCallback(() => {
    if (tourHistory.length === 0) return;
    const prev = tourHistory[tourHistory.length - 1];
    setTourHistory((h) => h.slice(0, -1));
    setIsTourTransitioning(true);
    setTimeout(() => {
      setActivePanorama({ ...prev, arrivedViaPortal: false });
      window.history.replaceState(null, "", `#pano=${prev.id}`);
      setTimeout(() => setIsTourTransitioning(false), 600);
    }, 420);
  }, [tourHistory]);

  // ── Effects ─────────────────────────────────────────────────────────────────

  // Deep-link hash router — fires on mount (handles direct URL) and on hashchange.
  useEffect(() => {
    if (!data || data.features.length === 0 || typeof window === "undefined") return;

    const handleHashRoute = () => {
      const hash   = window.location.hash.replace(/^#/, "");
      const params = new URLSearchParams(hash);
      const panoId = params.get("pano");

      if (panoId) {
        const feature = data.features.find((f) => (f.properties as any).id === panoId);
        if (!feature) return;

        const p = feature.properties as any;

        // Skip if already active or animation is in flight — use ref, not state, to avoid being a dep
        if (activePanoramaIdRef.current === p.id || isNavigatingRef.current) return;

        // Close current viewer so the map is visible before the fly-to begins
        if (activePanoramaIdRef.current) setActivePanorama(null);

        // hashchange fires only for external navigations (our internal replaceState calls don't trigger it)
        setViewerSource("external");

        if (mapViewerRef.current) {
          isNavigatingRef.current = true;
          const coords = feature.geometry?.coordinates as [number, number];
          mapViewerRef.current.navigateTo({
            ...featureToActivePanorama(p),
            coordinates: coords,
          });
        } else {
          // Fallback: map not yet mounted (early hydration)
          setActivePanorama(featureToActivePanorama(p));
        }
      } else {
        // Hash cleared → close the viewer
        isNavigatingRef.current = false;
        setActivePanorama(null);
      }
    };

    handleHashRoute();
    window.addEventListener("hashchange", handleHashRoute);
    return () => window.removeEventListener("hashchange", handleHashRoute);
  }, [data]); // activePanorama?.id intentionally omitted — read via ref to avoid re-triggering on open/close

  // Pending deep-link resolver — opens viewer immediately when data arrives.
  // pendingPanoramaId is cleared after use so closing the viewer doesn't re-trigger this.
  useEffect(() => {
    if (!pendingPanoramaId || !data || activePanoramaIdRef.current) return;
    const feature = data.features.find((f) => (f.properties as any).id === pendingPanoramaId);
    if (!feature) return;
    const p      = feature.properties as any;
    const coords = feature.geometry?.coordinates as [number, number];
    setViewerSource("external");
    setViewerOrigin({
      x: typeof window !== "undefined" ? window.innerWidth  / 2 : 400,
      y: typeof window !== "undefined" ? window.innerHeight / 2 : 300,
    });
    setActivePanorama(featureToActivePanorama(p));
    setPendingPanoramaId(null);
    if (coords) mapViewerRef.current?.jumpTo(coords);
  }, [data, pendingPanoramaId]);

  // Keep defaultYaw/Pitch/Zoom in sync when data refreshes (e.g. after an edit).
  useEffect(() => {
    if (!data || !activePanorama) return;
    const current = data.features.find((f) => (f.properties as any).id === activePanorama.id);
    if (!current) return;
    const p = current.properties as any;
    if (
      p.default_yaw   !== activePanorama.defaultYaw  ||
      p.default_pitch !== activePanorama.defaultPitch ||
      p.default_zoom  !== activePanorama.defaultZoom
    ) {
      setActivePanorama((prev) =>
        prev
          ? { ...prev, defaultYaw: p.default_yaw, defaultPitch: p.default_pitch, defaultZoom: p.default_zoom }
          : null
      );
    }
  }, [data, activePanorama?.id]);

  return {
    activePanorama, setActivePanorama,
    isHashChecked,
    pendingPanoramaId,
    viewerOrigin,
    viewerSource,
    tourManagerCollectionId,
    isTourTransitioning,
    tourHistory,    setTourHistory,
    mapViewerRef,
    handleSelectPanorama,
    handleOpenPanoramaFromDashboard,
    handleOpenPanoramaFromTourManager,
    handleTourNavigate,
    handleBack,
  };
}
